import tinycolor from 'tinycolor2';
import { api } from '@/core/http/apiClient';
import { CookieNames, cookies } from '@/core/cookies';
import themes from './themes.json';

/** The eight swatches offered in the profile menu (Angular HeaderComponent.colors). */
export const THEME_COLORS: readonly string[] = [
  '#6d41a1',
  '#6661b8',
  '#4461d7',
  '#a0144f',
  '#045b62',
  '#14539a',
  '#a76f05',
  '#000000',
];

export interface ThemeColor {
  name: string;
  hex: string;
  darkContrast: boolean;
}

interface ThemeDefinition {
  id: string;
  color50: string;
  color100: string;
  color200: string;
  color300: string;
}

function colorObject(value: string, name: string): ThemeColor {
  const c = tinycolor(value);
  return { name, hex: c.toHexString(), darkContrast: c.isLight() };
}

/** Lives for the whole session, like Angular's `primaryColorPalette` field: an unknown colour keeps the last palette. */
let palette: ThemeColor[] = [];

/**
 * Angular HeaderComponent.computeColors(). Its source loops to `Object.keys(themes).length - 2`, which reads like
 * "all but the last two"; in the BUILT app the JSON namespace import carries two extra keys, so the bound is 8
 * and every swatch matches (verified by building the Angular app; see PARITY_DECISIONS D2).
 */
export function computeColors(hex: string): void {
  const all = themes as ThemeDefinition[];
  for (let index = 0; index < all.length; index++) {
    if (all[index].id === hex) {
      const selected = all[index];
      palette = [
        colorObject(selected.id, '500'),
        colorObject(selected.color50, '50'),
        colorObject(selected.color100, '100'),
        colorObject(selected.color200, '200'),
        colorObject(selected.color300, '300'),
      ];
    }
  }

  for (const color of palette) {
    document.documentElement.style.setProperty(`--theme-primary-${color.name}`, color.hex);
  }
}

export function currentThemePalette(): readonly ThemeColor[] {
  return palette;
}

/** The colour the API remembered for this user (copied from the `themecolor` response header into a cookie). */
export function initialThemeColor(): string {
  return cookies.get(CookieNames.themeColor);
}

/** POST User/updateThemeColor with a JSON-quoted colour, as Angular CommonService.updateThemeColor. */
export function saveThemeColor(color: string): Promise<unknown> {
  return api.post('User/updateThemeColor', JSON.stringify(color), { headers: { 'Content-Type': 'application/json' } });
}
